/**
 * Fehlerzustand — sagt, was schiefging und was zu tun ist.
 *
 * Er entschuldigt sich nicht (`design-system`). „Die Objekte konnten nicht
 * geladen werden. Erneut versuchen." ist brauchbar; „Es tut uns leid, ein
 * Fehler ist aufgetreten" ist es nicht — daraus folgt keine Handlung.
 */

import { ApiError } from '@/lib/api'

interface ErrorStateProps {
  readonly error: unknown
  readonly onRetry: () => void
  readonly testId: string
  /** Meist `${testId}-retry` (Vorgabe unten) — Feature 01 nennt Baum und
   * Stammdaten aber eigenständig (`object-tree-retry`,
   * `property-master-data-retry`), unabhängig vom Namen des
   * Fehlerbehälters (`docs/specs/01-properties.md`, „Der Baum" /
   * „Reiter Stammdaten"). Explizit gesetzt weicht das vom Muster ab —
   * gespiegelt zum `retryIdentifier` in `Core/Design/EmptyStateView.swift`. */
  readonly retryTestId?: string
}

/**
 * Die Meldung der API hat Vorrang: Sie ist auf Deutsch formuliert und kennt den
 * Fall. Ein `TypeError` aus einem abgebrochenen `fetch` dagegen sagt dem Nutzer
 * nichts — dafür steht hier ein eigener Satz.
 */
function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  return 'Die Verbindung zum Server ist unterbrochen.'
}

export function ErrorState({ error, onRetry, testId, retryTestId }: ErrorStateProps) {
  return (
    <div
      data-testid={testId}
      role="alert"
      className="flex flex-col items-center justify-center gap-3 px-(--page-x) py-16 text-center"
    >
      <p className="text-section font-(--font-weight-section) text-text">{messageFor(error)}</p>
      <button
        type="button"
        onClick={onRetry}
        data-testid={retryTestId ?? `${testId}-retry`}
        className="border-border text-text hover:bg-bg focus-visible:border-accent rounded-card border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
      >
        Erneut versuchen
      </button>
    </div>
  )
}
